import type { RequestOptions } from '@@/plugin-request/request';
import { getLocale, history, type RequestConfig } from '@umijs/max';
import { Modal, message } from 'antd';
import type { APIResponse } from '@/api/typings';
import { clearToken, getToken } from '@/utils/token';
import { removeInvalid, removeNil } from './utils';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  msg: string | null;
  data: any;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { code, msg, data } = res as unknown as ResponseStructure;
      // code 不为 200 时抛出错误
      if (code !== 200) {
        const error: any = new Error(msg || '请求失败');
        error.name = 'BizError';
        error.info = { code, msg, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 打印错误信息到控制台
      console.error('【请求错误】', {
        请求地址:
          error.response?.headers?.['x-real-url'] ||
          error.response?.config?.url ||
          '未知',
        请求方法: error.response?.config?.method || '未知',
        请求参数:
          error.response?.config?.params ||
          error.response?.config?.data ||
          '无',
        错误类型: error.name,
        错误信息: error.message,
      });

      // 是否已在登录页：已在此页时 401 不再弹窗打扰（如登出后残留的在途请求）
      const onLoginPage =
        window.location.pathname === '/user/login' ||
        history.location.pathname === '/user/login';

      // 登录/登出接口的 401 是"凭证错误"（如密码输错）而非"会话过期"，
      // 不清理 token、不弹会话失效框，走普通错误提示
      const requestUrl: string = error.response?.config?.url || '';
      const isAuthEndpoint = ['/system/login', '/logout'].some((u) =>
        requestUrl.includes(u),
      );

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { msg } = errorInfo;
          // 根据 code 处理不同的错误
          switch (errorInfo.code) {
            case 401:
              if (isAuthEndpoint) {
                if (msg) {
                  message.error(msg);
                }
                break;
              }
              // 未授权，清除 token 并跳转登录
              clearToken();
              if (!onLoginPage) {
                Modal.warning({
                  title: '登录已失效',
                  content: '您的登录已失效，请重新登录',
                  okText: '确定',
                  onOk: () => {
                    history.push('/user/login');
                  },
                });
              }
              break;
            default:
              if (msg) {
                message.error(msg);
              }
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { status } = error.response;
        if (status === 401) {
          if (isAuthEndpoint) {
            // 如密码错误以 HTTP 401 返回：提示后端 msg，不当作会话失效处理
            message.error(
              error.response?.data?.msg || '登录失败，请检查账号或密码',
            );
          } else {
            clearToken();
            if (!onLoginPage) {
              Modal.warning({
                title: '登录已失效',
                content: '您的登录已失效，请重新登录',
                okText: '确定',
                onOk: () => {
                  history.push('/user/login');
                },
              });
            }
          }
        } else {
          message.error(`请求失败，状态码：${status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        message.error('网络请求失败，请检查网络连接');
      } else {
        // 发送请求时出了点问题
        message.error('请求错误，请重试');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 打印完整请求信息
      const baseURL = config.baseURL || window.location.origin;
      const fullURL = new URL(config.url || '', baseURL).href;
      console.log('【API请求】', {
        请求方法: config.method?.toUpperCase(),
        请求地址: fullURL,
        请求头: config.headers,
        请求参数: config.params,
        请求体: config.data,
      });

      // 拦截请求配置，进行个性化处理
      const localeMap: Record<string, string> = {
        'zh-CN': 'zh-CN',
        'en-US': 'en',
      };
      const locale = getLocale();
      // 显式传入的 Authorization 优先（如登出请求在清 token 后仍需携带原 token）。
      // 注意：依赖 axios<1.x 拦截器里 headers 为普通对象且保留键大小写；
      // 升级 axios 1.x（AxiosHeaders 归一化）后需重审此处读取方式
      const rawAuth = config.headers?.Authorization;
      const explicitAuth = typeof rawAuth === 'string' ? rawAuth : undefined;
      const headers: Record<string, string> = {
        ...config.headers,
        Authorization: explicitAuth || `Bearer ${getToken()}`,
        'Accept-Language': localeMap[locale] ?? locale,
      };

      if (config.method?.toLowerCase() === 'get' && config.params) {
        config.params.pageNum = config.params.current;
        Reflect.deleteProperty(config.params, 'current');
        config.params = removeInvalid(config.params);
      } else if (
        ['post', 'put']?.includes(config.method as any) &&
        config.data &&
        !(config.data instanceof FormData)
      ) {
        // 请求体保留显式空串:后端契约 ''=清空字段、缺省/null=不动旧值,
        // 剥掉 '' 会导致表单清空的值(如部门电话)保存后旧值复活。
        config.data = removeNil(config.data);
      }

      return { ...config, headers };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const { status, config } = response;

      // HTTP 状态码不是 2xx 时视为失败
      if (!status || status < 200 || status >= 300) {
        // 其他 HTTP 错误状态码，交给 errorHandler 统一处理
        const error: any = new Error(`请求失败，状态码：${status}`);
        error.response = response;
        return Promise.reject(error);
      }

      // 检查业务响应数据
      const { data } = response as unknown as APIResponse;
      if (typeof data === 'object' && !(data instanceof Blob)) {
        // 检查业务 code，不是 200 时抛出错误
        if (
          data &&
          typeof data === 'object' &&
          'code' in data &&
          data.code !== 200
        ) {
          const error: any = new Error(data.msg || '请求失败');
          error.name = 'BizError';
          error.info = { code: data.code, msg: data.msg, data: data.data };
          error.response = response;
          throw error; // 抛出业务错误，会被 errorHandler 捕获
        }

        // 处理 dropdown-list 接口：将 desc 改为 label，code 改为 value
        if (
          config?.url?.includes('/dropdown-list') &&
          Array.isArray(data?.data)
        ) {
          data.data = data.data.map((item: any) => ({
            label: item.desc,
            value: item.code,
            ...item,
          }));
        }
      }

      return response as any;
    },
  ],
};
